/**
 * flightDataUtils — ScheduleDraft 航班与航路原生字段读取工具层。
 *
 * 本模块只处理后端字段、route_snapshot 与前端兼容字段之间的读取优先级。
 * 航路引用数据、匹配与机型兼容语义集中在 routeUtils。
 *
 * 设计文档 → docs/10_core_contracts/10_data_model.md
 * 骨架关联 → lib/routeUtils.js (航路引用与匹配), components/editor/timetable/timetableRowModel.js (行模型), services/scheduleDraftPayloadBuilders.js (写入 payload)
 */

import { normalizeMinutes } from "./flightTimingUtils";

const EMPTY_LOCAL_FLIGHTS_MAP = new Map();

/**
 * 从航路对象中提取计划 block time（分钟）。
 * @param {Object|null} route
 * @returns {number|null}
 */
export function getRouteBlockTime(route) {
    return normalizeMinutes(route?.block_time_minutes);
}

/**
 * 从航路对象中提取最小 block time（分钟）。
 * @param {Object|null} route
 * @returns {number|null}
 */
export function getRouteMinBlockTime(route) {
    return normalizeMinutes(route?.min_block_time_minutes);
}

/**
 * 从航班对象中读取用于展示的航路标准 block time。
 * @param {Object|null} flight - ScheduleDraftFlight 或兼容航班对象。
 * @returns {number|null} 航路级 block time 分钟数。
 */
export function getFlightRouteBlockTime(flight) {
    return normalizeMinutes(flight?.flight_route_block_time_minutes)
        ?? normalizeMinutes(flight?.route_snapshot?.block_time_minutes);
}

function compactText(value) {
    const text = String(value ?? "").trim();
    return text || null;
}

function getRouteLikeExplicitFullName(routeLike) {
    return compactText(routeLike?.full_name)
        || compactText(routeLike?.fullName)
        || compactText(routeLike?.route_full_name)
        || compactText(routeLike?.routeFullName);
}

function getRouteLikeName(routeLike) {
    return compactText(routeLike?.route_name)
        || compactText(routeLike?.name);
}

function getRouteLikeCode(routeLike) {
    return compactText(routeLike?.route_code)
        || compactText(routeLike?.routeCode)
        || compactText(routeLike?.code);
}

function getRouteLikeSummary(routeLike, flight) {
    const departure = compactText(routeLike?.departure_airport) || compactText(flight?.departure_airport);
    const arrival = compactText(routeLike?.arrival_airport) || compactText(flight?.arrival_airport);
    return departure && arrival ? `${departure}-${arrival}` : null;
}

/**
 * 从航班对象中读取航路完整展示名。
 * @param {Object|null} flight - ScheduleDraftFlight 或兼容航班对象。
 * @returns {string|null} 航路完整名称。
 */
export function getFlightRouteFullName(flight) {
    return getRouteLikeExplicitFullName(flight?.route_snapshot)
        || compactText(flight?.flight_route_full_name)
        || compactText(flight?.flightRouteFullName)
        || compactText(flight?.route_full_name)
        || compactText(flight?.routeFullName)
        || getRouteLikeName(flight?.route_snapshot)
        || compactText(flight?.flight_route_name)
        || compactText(flight?.route_name)
        || compactText(flight?.routeName)
        || compactText(flight?.name);
}

/**
 * 从航班对象中读取航路 code 或航段摘要兜底。
 * @param {Object|null} flight - ScheduleDraftFlight 或兼容航班对象。
 * @returns {string|null} 航路 code 或 DEP-ARR 摘要。
 */
export function getFlightRouteSummary(flight) {
    return getRouteLikeCode(flight?.route_snapshot)
        || compactText(flight?.flight_route_code)
        || compactText(flight?.flightRouteCode)
        || compactText(flight?.route_code)
        || compactText(flight?.routeCode)
        || getRouteLikeSummary(flight?.route_snapshot, flight);
}

/**
 * 从航班对象中读取航路展示名称。
 * @param {Object|null} flight - ScheduleDraftFlight 或兼容航班对象。
 * @returns {string|null} 航路名称。
 */
export function getFlightRouteName(flight) {
    return getFlightRouteFullName(flight);
}

export function getModifiedStatus(flight) {
    return flight?.change_status === "ADDED" ? "ADDED" : "MODIFIED";
}

export function getSelectedLocalFlights(localFlightsMap = EMPTY_LOCAL_FLIGHTS_MAP, flightIds = []) {
    return flightIds
        .map((flightId) => localFlightsMap.get(String(flightId)))
        .filter((flight) => flight && String(flight.change_status || "").toUpperCase() !== "DELETED");
}
